// DeleteChatModal.tsx
import React, { useState } from "react";
import { useModalState } from "../../helpers/ModalProvider";
import { useGlobalState } from "../../helpers/GlobalStateProvider";
import { deleteConversation } from "../chatbot/chatHistoryStorage";
import { DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from "../../components/ui/dialog";
import { Stack, Text } from "@chakra-ui/react";
import { Button } from "../../components/ui/button";

const DeleteChatModal: React.FC = () => {
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    isDeleteChatModalVisible,
    deleteChatId,
    deleteChatName,
    closeDeleteChatModal,
  } = useModalState();

  const { setChatHistory, chatHistory, setSelectedChatId, setMessages, selectedChatId } =
    useGlobalState();

  const deleteChat = async (chatId: number) => {
    setIsDeleting(true);

    // Find the chat to get the agent conversation ID
    const chatToDelete = chatHistory.find((c: any) => c.id === chatId);
    const agentConversationId = chatToDelete?.agentConversationId;

    if (agentConversationId) {
      // Try to delete from the agent backend
      const success = await deleteConversation(agentConversationId);

      if (!success) {
        if (process.env.NODE_ENV === 'development') console.warn("Failed to delete conversation from backend, removing locally only");
      }
    }

    // Remove from local state (whether backend succeeded or not)
    setChatHistory((prev: any) =>
      prev.filter((chat: any) => chat.id !== chatId),
    );

    // If we're deleting the currently selected chat, switch to another one or clear
    if (chatId === selectedChatId) {
      const remainingChats = chatHistory.filter(
        (chat: any) => chat.id !== chatId,
      );
      if (remainingChats.length > 0) {
        setSelectedChatId(remainingChats[0].id);
      } else {
        setSelectedChatId(null);
        setMessages([]);
      }
    }

    setIsDeleting(false);
    closeDeleteChatModal();
  };

  const handleDelete = () => {
    if (deleteChatId !== null && !isDeleting) {
      deleteChat(deleteChatId);
    } else if (!isDeleting) {
      closeDeleteChatModal();
    }
  };

  return (
    <DialogRoot lazyMount open={isDeleteChatModalVisible} onOpenChange={(e) => { if (!e.open) closeDeleteChatModal(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete chat?</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>
        <DialogBody>
          <Stack gap={2}>
            <Text fontSize="md" color="#52525b" lineHeight="24px">
              Are you sure you want to delete{" "}
              <Text as="span" fontWeight="semibold" color="#18181b">{deleteChatName}</Text>?
            </Text>
            <Text fontSize="sm" color="#a1a1aa" lineHeight="20px">
              This will permanently delete this conversation and remove all
              memory from your device.
            </Text>
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={closeDeleteChatModal}>
            Cancel
          </Button>
          <Button colorPalette="red" onClick={handleDelete} loading={isDeleting}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default DeleteChatModal;
